/**
 * CSS 스코프 유틸리티
 * CSS 선택자에 특정 스코프를 추가하여 격리시킵니다.
 */

/**
 * CSS 규칙을 파싱하고 스코프를 추가합니다
 * @param {string} css - 원본 CSS 문자열
 * @param {string} scope - 추가할 스코프 (예: '[data-editor-scope="unique-id"]')
 * @param {Object} options - 추가 옵션
 * @param {boolean} options.preserveRootVars - :root의 CSS 변수를 보존할지 여부
 * @param {boolean} options.scopeKeyframes - @keyframes 이름에 스코프를 추가할지 여부
 * @returns {string} 스코프가 추가된 CSS
 */
export function scopeCSS(css, scope, options = {}) {
  if (!css || !scope) return css;
  
  const {
    preserveRootVars = false,
    scopeKeyframes = true
  } = options;
  
  // 스코프 ID 추출 (keyframes 스코핑에 사용)
  const scopeId = scope.match(/data-editor-scope="([^"]+)"/)?.[1] || 'scoped';

  // CSS 규칙을 파싱하기 위한 정규식
  const ruleRegex = /([^{]+)\s*\{([^}]*)\}/g;
  
  // @규칙들 (media, keyframes 등)을 임시로 저장
  const atRules = [];
  let processedCSS = css;
  
  // 애니메이션 이름 매핑 (keyframes 스코핑용)
  const animationNameMap = new Map();
  
  // :root 변수 보존 처리
  let rootVarsCSS = '';
  if (preserveRootVars) {
    processedCSS = processedCSS.replace(/:root\s*\{([^}]*)\}/g, (match, declarations) => {
      // CSS 변수만 추출
      const varDeclarations = declarations.match(/--[^:]+:[^;]+;/g);
      if (varDeclarations) {
        rootVarsCSS = `:root { ${varDeclarations.join(' ')} }`;
      }
      // 나머지 선언은 스코프 적용을 위해 유지
      const nonVarDeclarations = declarations.replace(/--[^:]+:[^;]+;/g, '').trim();
      return nonVarDeclarations ? `${scope} { ${nonVarDeclarations} }` : '';
    });
  }
  
  // @supports 규칙 처리
  processedCSS = processedCSS.replace(/@supports[^{]+\{([\s\S]+?)\}\s*\}/g, (match, content) => {
    const scopedContent = scopeCSS(content, scope, options);
    const scopedSupports = match.replace(content, scopedContent);
    atRules.push(scopedSupports);
    return `__AT_RULE_${atRules.length - 1}__`;
  });
  
  // @container 규칙 처리
  processedCSS = processedCSS.replace(/@container[^{]+\{([\s\S]+?)\}\s*\}/g, (match, content) => {
    const scopedContent = scopeCSS(content, scope, options);
    const scopedContainer = match.replace(content, scopedContent);
    atRules.push(scopedContainer);
    return `__AT_RULE_${atRules.length - 1}__`;
  });
  
  // @layer 규칙 처리
  processedCSS = processedCSS.replace(/@layer[^{]*\{([\s\S]+?)\}\s*\}/g, (match, content) => {
    const scopedContent = scopeCSS(content, scope, options);
    const scopedLayer = match.replace(content, scopedContent);
    atRules.push(scopedLayer);
    return `__AT_RULE_${atRules.length - 1}__`;
  });
  
  // @media 규칙 처리 (개선된 중첩 처리)
  const processMediaQuery = (css) => {
    return css.replace(/@media[^{]+\{([\s\S]+?)\}(?=\s*(?:@|[^}]|$))/g, (match, content) => {
      // 중첩된 미디어 쿼리 재귀 처리
      const processedContent = processMediaQuery(content);
      const scopedContent = scopeCSS(processedContent, scope, options);
      const scopedMedia = match.replace(content, scopedContent);
      atRules.push(scopedMedia);
      return `__AT_RULE_${atRules.length - 1}__`;
    });
  };
  processedCSS = processMediaQuery(processedCSS);
  
  // @keyframes 규칙 처리 (스코프 옵션에 따라)
  processedCSS = processedCSS.replace(/@keyframes\s+([^\s{]+)\s*\{([\s\S]+?)\}(?=\s*(?:@|[^}]|$))/g, (match, name, content) => {
    if (scopeKeyframes) {
      const scopedName = `${name}-${scopeId}`;
      animationNameMap.set(name, scopedName);
      const scopedKeyframes = `@keyframes ${scopedName} {${content}}`;
      atRules.push(scopedKeyframes);
    } else {
      atRules.push(match);
    }
    return `__AT_RULE_${atRules.length - 1}__`;
  });
  
  // 일반 CSS 규칙 처리
  processedCSS = processedCSS.replace(ruleRegex, (match, selectors, declarations) => {
    // 선택자를 쉼표로 분리
    const selectorList = selectors.split(',').map(selector => {
      selector = selector.trim();
      
      // 빈 선택자는 무시
      if (!selector) return '';
      
      // 이미 스코프가 있는지 확인
      if (selector.includes(scope)) return selector;
      
      // :root, html, body 같은 전역 선택자 처리
      if (selector === ':root') {
        // :root는 preserveRootVars 옵션에 따라 처리됨
        return preserveRootVars ? selector : scope;
      }
      
      if (selector === 'html' || selector === 'body') {
        return scope;
      }
      
      // * 선택자 처리
      if (selector === '*') {
        return `${scope} *`;
      }
      
      // 가상 클래스/요소가 있는 경우 처리
      const pseudoMatch = selector.match(/(:+[\w-]+(?:\([^)]*\))?)$/);
      if (pseudoMatch) {
        const basePart = selector.substring(0, selector.length - pseudoMatch[0].length);
        const pseudoPart = pseudoMatch[0];
        
        // 기본 부분이 비어있으면 스코프만 사용
        if (!basePart.trim()) {
          return scope + pseudoPart;
        }
        
        // 자손 결합자 처리
        if (basePart.includes(' ')) {
          return `${scope} ${basePart}${pseudoPart}`;
        }
        
        return `${scope} ${basePart}${pseudoPart}`;
      }
      
      // > 자식 선택자 처리
      if (selector.startsWith('>')) {
        return `${scope} ${selector}`;
      }
      
      // + 인접 형제 선택자 처리
      if (selector.startsWith('+')) {
        return `${scope} ${selector}`;
      }
      
      // ~ 일반 형제 선택자 처리
      if (selector.startsWith('~')) {
        return `${scope} ${selector}`;
      }
      
      // 일반 선택자
      return `${scope} ${selector}`;
    }).filter(s => s).join(', ');
    
    // animation-name 속성 변경 (keyframes 스코핑이 활성화된 경우)
    let modifiedDeclarations = declarations;
    if (scopeKeyframes && animationNameMap.size > 0) {
      modifiedDeclarations = declarations.replace(
        /animation(-name)?\s*:\s*([^;]+)/g,
        (match, namePart, value) => {
          // 애니메이션 이름들을 파싱하고 변경
          const names = value.split(/\s*,\s*/).map(part => {
            const namePart = part.trim().split(/\s+/)[0];
            return animationNameMap.has(namePart) 
              ? part.replace(namePart, animationNameMap.get(namePart))
              : part;
          });
          return `animation${namePart || ''}: ${names.join(', ')}`;
        }
      );
    }
    
    return selectorList ? `${selectorList} {${modifiedDeclarations}}` : '';
  });
  
  // @규칙들 복원
  atRules.forEach((rule, index) => {
    processedCSS = processedCSS.replace(`__AT_RULE_${index}__`, rule);
  });
  
  // 연속된 공백 제거 및 정리
  processedCSS = processedCSS
    .replace(/\s+/g, ' ')
    .replace(/\s*{\s*/g, ' { ')
    .replace(/\s*}\s*/g, ' } ')
    .replace(/\s*;\s*/g, '; ')
    .trim();
  
  // :root 변수를 맨 앞에 추가 (preserveRootVars 옵션이 활성화된 경우)
  if (rootVarsCSS) {
    processedCSS = rootVarsCSS + ' ' + processedCSS;
  }
  
  return processedCSS;
}

/**
 * 고유한 스코프 ID를 생성합니다
 * @param {string} prefix - ID 접두사 (선택사항)
 * @returns {string} 고유한 스코프 ID
 */
export function generateScopeId(prefix = 'editor') {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 9);
  return `${prefix}-${timestamp}-${random}`;
}

/**
 * CSS를 안전하게 적용합니다
 * @param {string} css - 적용할 CSS
 * @param {string} scopeId - 스코프 ID
 * @param {string} targetId - 스타일 엘리먼트 ID (선택사항)
 * @returns {HTMLStyleElement} 생성된 스타일 엘리먼트
 */
export function applyScopedCSS(css, scopeId, targetId = null, options = {}) {
  if (!css || !scopeId) return null;
  
  const scope = `[data-editor-scope="${scopeId}"]`;
  const scopedCSS = scopeCSS(css, scope, options);
  

  // 스타일 엘리먼트 생성 또는 업데이트
  const styleId = targetId || `scoped-style-${scopeId}`;
  let styleElement = document.getElementById(styleId);
  
  if (!styleElement) {
    styleElement = document.createElement('style');
    styleElement.id = styleId;
    styleElement.setAttribute('data-scoped', 'true');
    document.head.appendChild(styleElement);
  }
  
  styleElement.textContent = scopedCSS;
  
  return styleElement;
}

/**
 * 스코프된 스타일을 제거합니다
 * @param {string} scopeId - 제거할 스코프 ID
 */
export function removeScopedCSS(scopeId) {
  const styleId = `scoped-style-${scopeId}`;
  const styleElement = document.getElementById(styleId);
  
  if (styleElement) {
    styleElement.remove();
  }
}

/**
 * HTML에 스코프 속성을 추가합니다
 * @param {string} html - 원본 HTML
 * @param {string} scopeId - 추가할 스코프 ID
 * @returns {string} 스코프가 추가된 HTML
 */
export function addScopeToHTML(html, scopeId) {
  if (!html || !scopeId) return html;
  
  // 최상위 요소들을 div로 감싸고 스코프 속성 추가
  const wrappedHTML = `<div data-editor-scope="${scopeId}">${html}</div>`;
  
  return wrappedHTML;
}

/**
 * CSS에서 위험한 속성을 제거합니다 (보안)
 * @param {string} css - 검사할 CSS
 * @returns {string} 정제된 CSS
 */
export function sanitizeCSS(css) {
  if (!css) return css;
  
  // 위험한 속성들 제거
  const dangerousPatterns = [
    /javascript:/gi,
    /expression\s*\(/gi,
    /@import/gi,
    /behavior\s*:/gi,
    /-moz-binding\s*:/gi,
    /binding\s*:/gi,
  ];
  
  let sanitized = css;
  dangerousPatterns.forEach(pattern => {
    sanitized = sanitized.replace(pattern, '');
  });
  
  return sanitized;
}